import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Pagination,
  Paper,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  clearCsvReportData,
  getCsvReport,
  getEmployeeList,
  getPtoOptionListData,
  getPtoReport,
} from '../../../store/ReportsDetailsSlice/actions';
import {
  selectCsvReportData,
  selectEmployeeData,
  selectPtoOptionsList,
  selectPtoReportData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import { GROUPBY, OPTIONS } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: (theme.vars ?? theme).palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'start',
  height: '100%',
}));

const PAGINATION = 60;
const defaultFormValues = {
  emp_code: [],
  options: [],
  groupby: { id: 'Emp', name: 'Employee' },
};

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const ReportPtoScreen = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;
  const pageParam = params.get('page') || 1;
  const { fromDate, toDate, render } = useDateRangeFilter();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { ptoOptionsList, isPtoOptionsListLoading } =
    useSelector(selectPtoOptionsList);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });

  const { emp_code: employees, options, groupby } = watch();
  const employeeParam = useMemo(() => createParams(employees), [employees]);

  const { ptoReportData, loadingPtoReport } = useSelector(selectPtoReportData);
  const { csvPtoReport, loadingCsvPtoReport } =
    useSelector(selectCsvReportData);

  const pagesCount = Math.ceil(Object.keys(ptoReportData).length / PAGINATION);

  const tableData = useMemo(() => {
    return Object.entries(ptoReportData).slice(
      (+pageParam - 1) * PAGINATION,
      +pageParam * PAGINATION
    );
  }, [ptoReportData, pageParam]);
  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp:
        employeeParam.length === 1 && employeeParam[0] === 'ALL'
          ? undefined
          : employeeParam,
      arr_pto_type: options.map((item) => item.id),
    };
  }, [fromDate, toDate, groupby, employeeParam, options]);

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getCsvReport(query));
  };
  const handlePageChange = (_, val) => {
    setParams((prev) => {
      prev.set('page', val);
      return prev;
    });
  };

  const handleFindClick = () => dispatch(getPtoReport(param));

  useEffect(
    () => {
      dispatch(getPtoReport(param));
      dispatch(getEmployeeList());
      dispatch(getPtoOptionListData());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => {
      setParams((prev) => {
        prev.delete('page');
        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ptoReportData]
  );
  useEffect(() => {
    if (csvPtoReport.data) {
      saveCsvToFile(csvPtoReport.data, csvPtoReport.filename);
      dispatch(clearCsvReportData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPtoReport]);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={loadingCsvPtoReport}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
    {
      key: 'options',
      label: 'Options',
      show: OPTIONS.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={Object.entries(ptoOptionsList).map(([key, value]) => ({
            id: key,
            name: value,
          }))}
          name="options"
          label="Options"
          multiple
          allOption={{ id: 'all', name: 'All' }}
          loading={isPtoOptionsListLoading}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="PTO Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig.slice(0, 4)}
            columns={isTabletVerticalWidth ? 1 : 4}
          />
        </Grid>
        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
              flexDirection: isTabletVerticalWidth ? 'column' : 'row',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>

            <FilterPanel filters={filterConfig.slice(4)} columns={1} />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {loadingPtoReport && (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 h-100 d-flex justify-content-center align-items-center"
            >
              <CircularProgress />
            </Box>
          )}

          {!loadingPtoReport && (
            <Box>
              {groupByParam === 'Group' &&
                tableData.map((item) => {
                  const items = Object.entries(item[1].data);
                  return (
                    <Grid container spacing={4} key={item[0]} mb={4}>
                      <Grid item xs={12} gap={2}>
                        <Typography variant="h5" mb={2}>
                          Group {item[0]}
                        </Typography>
                        <Grid container xs={12} gap={4}>
                          <Grid container gap={4} size={4}>
                            <Typography variant="p">
                              Hours count: {item[1].hour_count}
                            </Typography>
                            <Typography variant="p">
                              Shift count: {item[1].shift_count}
                            </Typography>
                          </Grid>
                          <Grid container direction="row" gap={3}>
                            {Object.entries(item[1].absent).map((abs) => {
                              const optionName = OPTIONS.find(
                                (opt) => opt.value === abs[0]
                              )?.title;
                              return (
                                <Typography key={abs[0]}>
                                  {optionName}: {abs[1]}
                                </Typography>
                              );
                            })}
                          </Grid>
                        </Grid>
                        <Grid container spacing={3} className="my-2">
                          {items.map(([key, value], index) => (
                            <GridPersonItem
                              title={key}
                              value={value}
                              key={index}
                            />
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              {(groupByParam === 'Emp' || groupByParam === 'Date') && (
                <Grid container spacing={3} className="my-2">
                  {tableData.map(([key, value], index) => (
                    <GridPersonItem
                      title={key}
                      value={value}
                      key={index}
                      gridOptions={
                        groupByParam === 'Emp'
                          ? { xs: 12, md: 6, lg: 4 }
                          : { xs: 12, lg: 6 }
                      }
                    />
                  ))}
                </Grid>
              )}
            </Box>
          )}
          {pagesCount > 1 && !loadingPtoReport && (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 d-flex justify-content-center align-items-center"
            >
              <Pagination
                count={pagesCount}
                size="small"
                siblingCount={3}
                onChange={handlePageChange}
                page={+pageParam}
              />
            </Box>
          )}
        </Grid>
      </Box>
    </Paper>
  );
};

const GridPersonItem = ({
  title,
  value,
  gridOptions = { xs: 12, md: 6, lg: 4 },
}) => {
  const [hovered, setHovered] = useState('');

  const handleMouseOver = (val) => {
    setHovered(val);
  };
  const handleMouseLeave = () => {
    setHovered('');
  };
  return (
    <Grid item gap={2} {...gridOptions}>
      <Item>
        <Typography variant="p" fontSize={16} color={'black'} fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="p" fontSize={14} fontWeight={400}>
          Hours count: {value.hour_count}
        </Typography>
        <Typography variant="p" fontSize={14} fontWeight={400}>
          Shift count: {value.shift_count}
        </Typography>
        {value.absent && (
          <>
            <Typography variant="p" pt={1} fontSize={14} fontWeight={600}>
              Leave reason
            </Typography>
            <Grid container direction="row" rowGap={1} columnGap={2}>
              {Object.entries(value.absent).map((abs) => {
                const optionName = OPTIONS.find(
                  (opt) => opt.value === abs[0]
                )?.title;
                return (
                  <Button
                    onMouseOver={() => handleMouseOver(optionName)}
                    onMouseLeave={handleMouseLeave}
                    padding={0}
                  >
                    <Typography
                      key={abs[0]}
                      color={'darkgray'}
                      fontWeight={600}
                    >
                      {optionName}: {abs[1]}
                    </Typography>
                  </Button>
                );
              })}
            </Grid>
          </>
        )}

        <Grid container gap={1} mt={1}>
          <Typography variant="p" fontSize={14} fontWeight={600}>
            Leaves:
          </Typography>
          {value?.data?.map((d, index) => (
            <Grid
              container
              direction="row"
              alignItems="stretch"
              key={index}
              color={
                hovered && hovered === d[2]
                  ? 'black'
                  : hovered
                    ? 'darkgray'
                    : undefined
              }
            >
              <Grid item xs={4} textAlign="justify" fontSize={12}>
                {d[0]}
              </Grid>
              <Grid item xs={4} textAlign="justify" fontSize={12}>
                {d[1]}
              </Grid>
              <Grid item xs={4} textAlign="justify" fontSize={12}>
                {d[2]}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Item>
    </Grid>
  );
};

export default ReportPtoScreen;
